import { useTheme } from '@mui/material';
import React from 'react';
const LISTBOX_ANIMATION_DURATION = 350;
const MAX_LISTBOX_HEIGHT = 360;
/**
 * Dynamically adjusts the listbox height based on viewport visibility.
 *
 * This hook monitors whether the autocomplete's paper element extends outside the viewport
 * and calculates an appropriate maximum height for the listbox by accounting for other
 * children elements within the paper container. When the listbox would overflow the viewport,
 * it reduces the max height to ensure the content remains visible.
 */
export function useListboxHeight(state) {
    const theme = useTheme();
    const [isOutsideViewport, setIsOutsideViewport] = React.useState(false);
    const [height, setHeight] = React.useState(0);
    const paperRef = React.useRef(null);
    React.useEffect(() => {
        if (isOutsideViewport || state !== 'open') {
            if (state === 'closed') {
                const timeout = setTimeout(() => {
                    setIsOutsideViewport(false);
                }, 0);
                return () => clearTimeout(timeout);
            }
            return;
        }
        const intersectionObserver = globalThis.IntersectionObserver
            ? new globalThis.IntersectionObserver(([{ target: node, intersectionRatio, isIntersecting }]) => {
                const isOutside = !isIntersecting || intersectionRatio < 1;
                const listHeight = node
                    .querySelector('[role="listbox"]')
                    ?.getBoundingClientRect().height;
                if (!listHeight) {
                    return;
                }
                const nodeSize = node.getBoundingClientRect();
                const diff = nodeSize.top + nodeSize.height - window.innerHeight;
                const spacing = parseFloat(theme.spacing(2));
                const newHeight = Math.floor(listHeight - diff - spacing);
                setHeight(newHeight);
                setIsOutsideViewport(isOutside);
            }, {
                root: null,
                rootMargin: '0px',
                threshold: 0
            })
            : null;
        const intervalId = setInterval(() => {
            if (!paperRef.current) {
                return;
            }
            clearInterval(intervalId);
            intersectionObserver?.observe(paperRef.current);
        }, LISTBOX_ANIMATION_DURATION);
        return () => {
            clearInterval(intervalId);
            intersectionObserver?.disconnect();
        };
    }, [isOutsideViewport, state, theme]);
    const observedHeight = isOutsideViewport ? height : undefined;
    const maxHeight = observedHeight !== undefined
        ? Math.min(observedHeight, MAX_LISTBOX_HEIGHT)
        : MAX_LISTBOX_HEIGHT;
    return { paperRef, maxHeight };
}
